import { useCallback, useEffect, useRef, useState } from "react";

/** How close to the end, in px, still counts as having scrolled back down to it. */
const AT_END_PX = 48;

export interface ScrollFollow {
  /** Callback ref for the scrolling element. */
  ref: (el: HTMLElement | null) => void;
  /** The reader scrolled up away from the end, so what is added there is no longer followed. */
  away: boolean;
  /** Something new landed at the end (`landed`) while the reader was away, and they have not been back. */
  unseen: boolean;
  /** Something new landed at the end - an answer. Unseen if the reader is away from it. */
  landed: () => void;
  /** Back to the end, following again: smooth for a click, at once otherwise. */
  toEnd: (smooth?: boolean) => void;
}

/**
 * Keeps a chat at its newest line while the reader is there, and only then.
 *
 * Whether they are is read from how they scroll - up and away from the end lets go, back down
 * to it follows again - never from how far the content jumped. So a tall jump (a table, a
 * picture, a long prompt) is still followed, and an older answer being read is never pulled
 * away from. Growth is watched with observers rather than effects, so an answer streaming in
 * a child component, or a picture decoding, is followed before it is painted.
 *
 * `active` false (an empty welcome screen, say) follows nothing.
 */
export function useScrollFollow(active = true): ScrollFollow {
  const [el, setEl] = useState<HTMLElement | null>(null);
  const followingRef = useRef(true);
  const activeRef = useRef(active);
  activeRef.current = active;
  const [away, setAway] = useState(false);
  const [unseen, setUnseen] = useState(false);

  const setFollowing = useCallback((following: boolean) => {
    followingRef.current = following;
    setAway(!following);
    if (following) setUnseen(false);
  }, []);

  useEffect(() => {
    if (!el) return;
    let lastTop = el.scrollTop;

    // Where the reader went since last looked at. Up and off the end is the reader leaving it;
    // up onto the end is the browser clamping after the content shrank (the final answer
    // replacing the stream); down to near the end is the reader coming back. Not moving changes
    // nothing, so a wheel that has let go is not taken back before its scroll lands.
    const readScroll = () => {
      const top = el.scrollTop;
      const fromEnd = el.scrollHeight - top - el.clientHeight;
      if (top < lastTop - 1) {
        if (fromEnd > 2) {
          if (followingRef.current) setFollowing(false);
        } else if (!followingRef.current) {
          setFollowing(true);
        }
      } else if (top > lastTop + 1 && fromEnd <= AT_END_PX && !followingRef.current) {
        setFollowing(true);
      }
      lastTop = top;
    };

    // A wheel turned up lets go at once: its scroll event comes a frame later, and an answer
    // streaming in meanwhile would otherwise pull the view straight back down.
    const onWheel = (event: WheelEvent) => {
      if (event.deltaY < 0 && followingRef.current && el.scrollHeight - el.clientHeight > AT_END_PX) {
        setFollowing(false);
      }
    };

    const onChange = () => {
      // The scroll event of a move the reader just made may still be on its way.
      readScroll();
      if (!activeRef.current || !followingRef.current) return;
      el.scrollTop = el.scrollHeight;
      lastTop = el.scrollTop;
    };

    // The element itself (the composer growing shrinks it) and each child (a picture decoding
    // grows one without any DOM change). Children come and go with the messages.
    const resize = new ResizeObserver(onChange);
    resize.observe(el);
    const watched = new WeakSet<Element>();
    const watchChildren = () => {
      for (const child of el.children) {
        if (watched.has(child)) continue;
        watched.add(child);
        resize.observe(child);
      }
    };
    watchChildren();
    const mutation = new MutationObserver((records) => {
      for (const record of records) {
        if (record.target !== el) continue;
        for (const node of record.removedNodes) {
          if (node instanceof Element) resize.unobserve(node);
        }
      }
      watchChildren();
      onChange();
    });
    mutation.observe(el, { childList: true, subtree: true, characterData: true });
    el.addEventListener("scroll", readScroll, { passive: true });
    el.addEventListener("wheel", onWheel, { passive: true });
    onChange();
    return () => {
      el.removeEventListener("scroll", readScroll);
      el.removeEventListener("wheel", onWheel);
      mutation.disconnect();
      resize.disconnect();
    };
  }, [el, setFollowing]);

  const landed = useCallback(() => {
    if (!followingRef.current) setUnseen(true);
  }, []);

  const toEnd = useCallback((smooth = false) => {
    setFollowing(true);
    el?.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, [el, setFollowing]);

  return { ref: setEl, away, unseen, landed, toEnd };
}
